import React, { useState } from 'react';
import { Map, Route, MapPin, X, ChevronRight, Activity, Save, Clock, Navigation2, Eye, Coffee, Dumbbell } from 'lucide-react';

const MapControls = ({ 
  tracking,
  startTracking,
  stopTracking,
  saveRoute,
  toggleRouteHistory,
  setPlanningMode,
  planningMode,
  plannedRoute,
  setShowNearby,
  showNearby,
  startPlannedRoute,
  cancelPlanning,
  selectedPlaceType,
  setSelectedPlaceType
}) => {
  const [controlsOpen, setControlsOpen] = useState(false);
  const [placeTypesOpen, setPlaceTypesOpen] = useState(false);

  const toggleControls = () => {
    setControlsOpen(!controlsOpen);
    if (placeTypesOpen) setPlaceTypesOpen(false);
  };

  const togglePlaceTypes = () => {
    setPlaceTypesOpen(!placeTypesOpen);
  };

  const placeTypes = [
    { id: 'park', label: 'Parks', icon: <MapPin size={16} /> },
    { id: 'hiking', label: 'Trails', icon: <Route size={16} /> },
    { id: 'gym', label: 'Gyms', icon: <Dumbbell size={16} /> },
    { id: 'cafe', label: 'Cafés', icon: <Coffee size={16} /> },
    { id: 'rest_stop', label: 'Rest Stops', icon: <MapPin size={16} /> }
  ];

  const handlePlaceTypeSelect = (type) => {
    setSelectedPlaceType(type);
    setShowNearby(true);
  };

  return (
    <>
      {/* Mobile Controls - Floating Panel */}
      <div className=" fixed z-20">
        {/* Toggle Button */}
        <button 
          onClick={toggleControls}
          className="absolute left-4 bottom-40 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center text-primary border-2 border-primary transition-all duration-300 hover:scale-110"
          aria-label="Toggle map controls"
        >
          {controlsOpen ? <X size={20} /> : <ChevronRight size={20} />}
        </button>

        {/* Controls Panel */}
        <div 
          className={`absolute left-4 bottom-52 bg-white rounded-xl shadow-xl p-3 transform transition-all duration-300 ${
            controlsOpen ? 'translate-x-0 opacity-100' : '-translate-x-full opacity-0'
          }`}
          style={{
            width: '200px',
            marginLeft: controlsOpen ? '48px' : '0'
          }}
        >
          <div className="flex flex-col space-y-2">
            <h3 className="text-sm font-bold text-gray-700 px-1 mb-1">Controls</h3>
            
            {/* Tracking Controls */}
            <div className="space-y-2">
              {!tracking ? (
                <button 
                  onClick={startTracking}
                  className="w-full bg-primary text-white py-2 px-3 rounded-lg flex items-center justify-center space-x-2 shadow-sm hover:bg-primary/90 transition-colors"
                >
                  <Route size={18} />
                  <span>Start Route</span>
                </button>
              ) : (
                <div className="flex flex-col space-y-2">
                  <button 
                    onClick={stopTracking}
                    className="w-full bg-red-500 text-white py-2 px-3 rounded-lg flex items-center justify-center space-x-2 shadow-sm hover:bg-red-600 transition-colors"
                  >
                    <X size={18} />
                    <span>Stop</span>
                  </button>
                  <button 
                    onClick={saveRoute}
                    className="w-full bg-secondary text-white py-2 px-3 rounded-lg flex items-center justify-center space-x-2 shadow-sm hover:bg-secondary/90 transition-colors"
                  >
                    <Save size={18} />
                    <span>Save Route</span>
                  </button>
                </div>
              )}
            </div>

            {/* Route Planning Controls */}
            <div className="space-y-2 pt-1 border-t border-gray-200">
              {planningMode ? (
                <button
                  onClick={cancelPlanning}
                  className="w-full bg-red-100 text-red-700 py-2 px-3 rounded-lg flex items-center justify-center space-x-2 shadow-sm hover:bg-red-200 transition-colors"
                >
                  <X size={18} />
                  <span>Cancel Planning</span>
                </button>
              ) : !plannedRoute ? (
                <button
                  onClick={() => setPlanningMode(true)}
                  className="w-full bg-blue-100 text-blue-700 py-2 px-3 rounded-lg flex items-center justify-center space-x-2 shadow-sm hover:bg-blue-200 transition-colors"
                >
                  <MapPin size={18} />
                  <span>Plan New Route</span>
                </button>
              ) : null}
            </div>

            {/* Place Types Controls */}
            <div className="space-y-2 pt-1 border-t border-gray-200">
              <button 
                onClick={togglePlaceTypes}
                className={`w-full py-2 px-3 rounded-lg flex items-center justify-center space-x-2 shadow-sm transition-colors ${
                  showNearby 
                  ? 'bg-blue-500 text-white hover:bg-blue-600' 
                  : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                }`}
              >
                <Eye size={18} />
                <span>{showNearby ? 'Showing Places' : 'Show Places'}</span>
              </button>
              
              {/* Place Types Dropdown */}
              {placeTypesOpen && (
                <div className="bg-white rounded-lg shadow-md p-2 mt-1">
                  <p className="text-xs text-gray-500 mb-2">Select place type:</p>
                  <div className="space-y-1">
                    {placeTypes.map(type => (
                      <button
                        key={type.id}
                        onClick={() => handlePlaceTypeSelect(type.id)}
                        className={`w-full py-1 px-2 text-left rounded flex items-center space-x-2 ${
                          selectedPlaceType === type.id 
                            ? 'bg-blue-100 text-blue-700' 
                            : 'hover:bg-gray-100'
                        }`}
                      >
                        {type.icon}
                        <span className="text-sm">{type.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              <button
                onClick={toggleRouteHistory}
                className=" md:hidden w-full bg-purple-100 text-purple-700 py-2 px-3 rounded-lg flex items-center justify-center space-x-2 shadow-sm hover:bg-purple-200 transition-colors"
              >
                <Clock size={18} />
                <span>Route History</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Desktop Place Type Selection */}
      {/* <div className="hidden md:block absolute right-4 top-16 bg-white rounded-lg shadow-md p-2">
        <div className="p-2">
          <p className="text-sm font-medium mb-2">Show Nearby:</p>
          <div className="flex flex-wrap gap-2">
            {placeTypes.map(type => (
              <button
                key={type.id}
                onClick={() => handlePlaceTypeSelect(type.id)}
                className={`py-1 px-3 rounded-full flex items-center space-x-2 text-sm ${
                  selectedPlaceType === type.id && showNearby
                    ? 'bg-blue-500 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {type.icon}
                <span>{type.label}</span>
              </button>
            ))}
            {showNearby && (
              <button
                onClick={() => setShowNearby(false)}
                className="py-1 px-3 rounded-full bg-red-100 text-red-700 hover:bg-red-200 text-sm"
              >
                <X size={16} />
                <span className="ml-1">Clear</span>
              </button>
            )}
          </div>
        </div>
      </div> */}
    </>
  );
};

export default MapControls;